import React, { useMemo, useEffect, JSX } from "react";
import { Modal } from "../../components/Modal";
import { useCardStatusStore } from "../../stores/cardStatusStore";
import { useKycStore } from "../../stores/useKycStore";
import KycPendingViaRain from "./KycPendingViaRain";
import PreRainTerms from "./PreRainTerms";
import { SumSubWidget } from "../../components/SumSubWidget";

interface KycModalProps {
  eoaAddress: string;
  onClose?: () => void;
  onComplete?: () => void;
}

export function KycModal({
  eoaAddress,
  onClose,
  onComplete,
}: KycModalProps): JSX.Element | null {
  const { status } = useCardStatusStore();
  const { acceptedRainTerms } = useKycStore();

  // Call onComplete when status becomes approved
  useEffect(() => {
    if (status === "approved" && onComplete) {
      onComplete();
    }
  }, [status, onComplete]);

  const getModalView = useMemo(() => {
    switch (status) {
      case "userNotExisting":
      case "feePending":
      case "initiated": {
        return {
          title: "User Verification",
          component: <SumSubWidget eoaAddress={eoaAddress} />,
        };
      }
      case "review": {
        return {
          title: "User Verification is in Process",
          component: <KycPendingViaRain />,
        };
      }
      case "pending": {
        if (acceptedRainTerms) {
          return {
            title: "User Verification is in Process",
            component: <KycPendingViaRain />,
          };
        } else {
          return {
            title: "User Verification is in Process",
            component: <PreRainTerms />,
          };
        }
      }
      case "approved": {
        // For approved status, we just return null as this SDK doesn't handle card activation
        return {
          title: "",
          component: <></>,
        };
      }
      case "rejected": {
        // For approved status, we just return null as this SDK doesn't handle card activation
        return {
          title: "",
          component: <></>,
        };
      }
      default:
        return {
          title: "Apply for Card",
          component: <></>,
        };
    }
  }, [status, acceptedRainTerms]);

  const handleClose = onClose || (() => {});
  const { title, component } = getModalView;

  // Don't render modal if status is approved or no component
  if (status === "approved" || !component) {
    return null;
  }

  return (
    <Modal title={title} onClose={handleClose}>
      {component}
    </Modal>
  );
}
